import type { MelodyPitchId } from './melodiaBuilderConfig';

/** Notas del rango vocal infantil (La3–Do5). */
export type SingPitchId = 'La3' | 'Si3' | MelodyPitchId;

export interface SingPitchDef {
  id: SingPitchId;
  hz: number;
  midi: number;
  label: string;
}

export const SING_PITCHES: SingPitchDef[] = [
  { id: 'Do5', hz: 523.25, midi: 72, label: 'Do5' },
  { id: 'Si4', hz: 493.88, midi: 71, label: 'Si4' },
  { id: 'La4', hz: 440.0, midi: 69, label: 'La4' },
  { id: 'Sol4', hz: 392.0, midi: 67, label: 'Sol4' },
  { id: 'Fa4', hz: 349.23, midi: 65, label: 'Fa4' },
  { id: 'Mi4', hz: 329.63, midi: 64, label: 'Mi4' },
  { id: 'Re4', hz: 293.66, midi: 62, label: 'Re4' },
  { id: 'Do4', hz: 261.63, midi: 60, label: 'Do4' },
  { id: 'Si3', hz: 246.94, midi: 59, label: 'Si3' },
  { id: 'La3', hz: 220.0, midi: 57, label: 'La3' },
];

export const SING_PITCH_BY_ID = Object.fromEntries(
  SING_PITCHES.map((p) => [p.id, p])
) as Record<SingPitchId, SingPitchDef>;

export const SING_MIN_MIDI = 57;
export const SING_MAX_MIDI = 72;

export type ClingPlayMode = 'exploracion' | 'canto';

export interface ClingPlayModeDef {
  id: ClingPlayMode;
  title: string;
  description: string;
  emoji: string;
}

export const CLING_PLAY_MODES: ClingPlayModeDef[] = [
  {
    id: 'exploracion',
    title: 'Exploración',
    description: 'El mapa avanza — canta para volar y recoge elementos',
    emoji: '🗺️',
  },
  {
    id: 'canto',
    title: 'Canto',
    description: 'El mapa avanza — canta las notas en el momento justo',
    emoji: '🎤',
  },
];

export interface ClingMelodyNote {
  pitchId: SingPitchId;
  /** Tiempo de inicio en beats desde el compás 1. */
  beat: number;
  durationBeats: number;
}

export interface ClingMelodyExercise {
  id: string;
  title: string;
  description: string;
  emoji: string;
  bpm: number;
  /** Carril vertical visible en el piano roll. */
  lanes: SingPitchId[];
  notes: ClingMelodyNote[];
}

export const CLING_MELODY_EXERCISES: ClingMelodyExercise[] = [
  {
    id: 'escala-do',
    title: 'Escala de Do',
    description: 'Sube paso a paso: Do Re Mi Sol La Do',
    emoji: '🎵',
    bpm: 72,
    lanes: ['Do5', 'La4', 'Sol4', 'Mi4', 'Re4', 'Do4'],
    notes: [
      { pitchId: 'Do4', beat: 0, durationBeats: 1 },
      { pitchId: 'Re4', beat: 1, durationBeats: 1 },
      { pitchId: 'Mi4', beat: 2, durationBeats: 1 },
      { pitchId: 'Sol4', beat: 3, durationBeats: 1 },
      { pitchId: 'La4', beat: 4, durationBeats: 1 },
      { pitchId: 'Do5', beat: 5, durationBeats: 1.5 },
    ],
  },
  {
    id: 'lucero',
    title: 'Brilla lucero',
    description: 'Sol Mi Sol Do — como una estrellita',
    emoji: '⭐',
    bpm: 80,
    lanes: ['Do5', 'Sol4', 'Mi4', 'Re4', 'Do4'],
    notes: [
      { pitchId: 'Sol4', beat: 0, durationBeats: 1 },
      { pitchId: 'Mi4', beat: 1, durationBeats: 1 },
      { pitchId: 'Sol4', beat: 2, durationBeats: 1 },
      { pitchId: 'Do5', beat: 3, durationBeats: 2 },
    ],
  },
  {
    id: 'sube-baja',
    title: 'Sube y baja',
    description: 'Do Mi Sol Mi Do — ondas suaves',
    emoji: '🌊',
    bpm: 76,
    lanes: ['Do5', 'Sol4', 'Mi4', 'Do4'],
    notes: [
      { pitchId: 'Do4', beat: 0, durationBeats: 1 },
      { pitchId: 'Mi4', beat: 1, durationBeats: 1 },
      { pitchId: 'Sol4', beat: 2, durationBeats: 1 },
      { pitchId: 'Mi4', beat: 3, durationBeats: 1 },
      { pitchId: 'Do4', beat: 4, durationBeats: 2 },
    ],
  },
];

export function exerciseDurationMs(exercise: ClingMelodyExercise): number {
  const last = exercise.notes.reduce(
    (max, n) => Math.max(max, n.beat + n.durationBeats),
    0
  );
  const beatMs = 60000 / exercise.bpm;
  return last * beatMs + 1200;
}

export function melodyPitchFromSing(id: SingPitchId): MelodyPitchId | null {
  if (id === 'La3' || id === 'Si3') return null;
  return id;
}
